import React, { useState } from 'react';
import { ChevronLeft, Calculator, GraduationCap, Book, FlaskConical, Percent } from 'lucide-react';
import CGPACalculator from '../../calculators/CGPACalculator';
import SemesterCalculator from '../../calculators/SemesterCalculator';
import TheoryCalculator from '../../calculators/TheoryCalculator';
import LabCalculator from '../../calculators/LabCalculator';
import PercentageCalculator from '../../calculators/PercentageCalculator';
import GradeScalePanel from '../../common/GradeScalePanel';

const GPACalculatorApp = ({ onBack }) => {
  const [currentTool, setCurrentTool] = useState(null);

  const tools = [
    { 
      id: 'cgpa', 
      title: 'CGPA Calculator', 
      description: 'Calculate cumulative GPA across all semesters.',
      icon: GraduationCap,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50 dark:bg-indigo-500/10'
    },
    { 
      id: 'semester', 
      title: 'Semester GPA', 
      description: 'Calculate GPA for a single semester.',
      icon: Calculator,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-500/10'
    },
    { 
      id: 'theory', 
      title: 'Theory GPA', 
      description: 'Single theory course marks to GPA.',
      icon: Book,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-500/10'
    },
    { 
      id: 'lab', 
      title: 'Lab & Theory', 
      description: 'Combined lab and theory course GPA.',
      icon: FlaskConical,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50 dark:bg-emerald-500/10'
    },
    { 
      id: 'percentage', 
      title: 'Percentage Tools', 
      description: 'Convert marks to percentages and back.',
      icon: Percent,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50 dark:bg-amber-500/10'
    },
  ];

  const renderTool = () => {
    switch (currentTool) {
      case 'cgpa': return <CGPACalculator />;
      case 'semester': return <SemesterCalculator />;
      case 'theory': return <TheoryCalculator />;
      case 'lab': return <LabCalculator />;
      case 'percentage': return <PercentageCalculator />;
      default: return null;
    }
  };

  if (currentTool) {
    const activeTool = tools.find(t => t.id === currentTool);
    return (
      <div className="animate-in fade-in slide-in-from-right-4 duration-500">
        <button 
          onClick={() => setCurrentTool(null)}
          className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-bold text-sm mb-6 transition-colors group"
        >
          <ChevronLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          Back to Calculators
        </button>

        <div className="bg-white dark:bg-[#0f1535] rounded-[2rem] p-6 sm:p-8 border border-slate-100 dark:border-indigo-500/10 shadow-sm mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className={`p-3 ${activeTool.bgColor} ${activeTool.color} rounded-2xl`}>
              <activeTool.icon size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                {activeTool.title}
              </h3>
              <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">
                {activeTool.description}
              </p>
            </div>
          </div>
          
          <div className="dark:text-white">
            {renderTool()}
          </div>
        </div>

        <GradeScalePanel />
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-left-4 duration-500">
      <button 
        onClick={onBack}
        className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-bold text-sm mb-6 transition-colors group"
      >
        <ChevronLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
        Back to Utilities
      </button>

      <div className="mb-8">
        <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
          GPA Calculator Suite
        </h2>
        <p className="text-slate-500 dark:text-slate-400 font-medium">
          Select the specific tool you need for your calculations.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tools.map((tool) => (
          <button
            key={tool.id}
            onClick={() => setCurrentTool(tool.id)}
            className="flex flex-col items-start p-6 bg-white dark:bg-[#0f1535] rounded-[2rem] border-2 border-transparent hover:border-indigo-500/20 shadow-sm hover:shadow-xl transition-all text-left group"
          >
            <div className={`p-3 ${tool.bgColor} ${tool.color} rounded-2xl mb-4 group-hover:scale-110 transition-transform`}>
              <tool.icon size={20} />
            </div>
            <h4 className="font-black text-slate-900 dark:text-white mb-1">{tool.title}</h4>
            <p className="text-slate-500 dark:text-slate-400 text-xs font-medium leading-relaxed">
              {tool.description}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
};

export default GPACalculatorApp;
